import React, { useState } from 'react';
import './BorrowingSystem.css';

const BorrowingSystem = ({ books, readers, borrowings, onBack, onBorrowingUpdate }) => {
  const [currentMode, setCurrentMode] = useState('checkout'); // 'checkout', 'return', 'renew', 'overdue'
  const [selectedReader, setSelectedReader] = useState('');
  const [selectedBook, setSelectedBook] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Debug: Log the readers data
  console.log('=== BorrowingSystem Debug ===');
  console.log('Readers count:', readers.length);
  console.log('Readers data:', readers.map(r => ({
    id: r.id,
    reader_number: r.reader_number,
    display_name: r.display_name,
    student_number: r.student_number,
    class_info: r.class_info,
    user_email: r.user?.email
  })));
  
  // Check if display_name exists
  const readersWithDisplayName = readers.filter(r => r.display_name && r.display_name !== 'Unknown Reader');
  console.log('Readers with display_name:', readersWithDisplayName.length);
  console.log('Readers without display_name:', readers.filter(r => !r.display_name || r.display_name === 'Unknown Reader').length);

  const availableBooks = books.filter(book => book.status === 'available');
  const borrowedBooks = books.filter(book => book.status === 'borrowed');
  const overdueBooks = borrowings.filter(borrowing => {
    const dueDate = new Date(borrowing.due_date || borrowing.dueDate);
    const today = new Date();
    return dueDate < today && borrowing.status === 'borrowed';
  });

  const handleCheckout = async () => {
    if (!selectedReader || !selectedBook) {
      alert('請選擇讀者和圖書');
      return;
    }

    setIsProcessing(true);
    
    // Simulate API call
    setTimeout(() => {
      const newBorrowing = {
        id: `BR${Date.now()}`,
        bookId: selectedBook,
        readerId: selectedReader,
        borrowDate: new Date().toISOString().split('T')[0],
        dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 14 days
        returnDate: null,
        status: 'borrowed',
        renewed: false,
        fineAmount: 0,
        notes: ''
      };

      const updatedBorrowings = [...borrowings, newBorrowing];
      onBorrowingUpdate(updatedBorrowings);

      // Update book status
      const updatedBooks = books.map(book => 
        book.id === selectedBook ? { ...book, status: 'borrowed' } : book
      );

      setSelectedBook('');
      setSelectedReader('');
      setIsProcessing(false);
      alert('借書成功！');
    }, 1000);
  };

  const handleReturn = async (borrowingId) => {
    setIsProcessing(true);
    
    setTimeout(() => {
      const updatedBorrowings = borrowings.map(borrowing => 
        borrowing.id === borrowingId 
          ? { ...borrowing, status: 'returned', returnDate: new Date().toISOString().split('T')[0] }
          : borrowing
      );

      // Update book status
      const borrowing = borrowings.find(b => b.id === borrowingId);
      const updatedBooks = books.map(book => 
        book.id === borrowing.bookId ? { ...book, status: 'available' } : book
      );

      onBorrowingUpdate(updatedBorrowings);
      setIsProcessing(false);
      alert('還書成功！');
    }, 1000);
  };

  const handleRenew = async (borrowingId) => {
    setIsProcessing(true);
    
    setTimeout(() => {
      const updatedBorrowings = borrowings.map(borrowing => 
        borrowing.id === borrowingId 
          ? { 
              ...borrowing, 
              dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              renewed: true 
            }
          : borrowing
      );

      onBorrowingUpdate(updatedBorrowings);
      setIsProcessing(false);
      alert('續借成功！');
    }, 1000);
  };

  const calculateFine = (dueDate) => {
    const due = new Date(dueDate);
    const today = new Date();
    const diffTime = today - due;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 0 ? diffDays * 2 : 0; // $2 per day
  };

  const getReaderName = (readerId) => {
    const reader = readers.find(r => r.id === readerId);
    return reader ? (reader.display_name || reader.user?.email || '未知讀者') : '未知讀者';
  };

  const getBookTitle = (bookId) => {
    const book = books.find(b => b.id === bookId);
    return book ? book.title : '未知圖書';
  };

  const getBookCallNumber = (bookId) => {
    const book = books.find(b => b.id === bookId);
    return book ? (book.call_number || book.callNumber || 'N/A') : 'N/A';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('zh-HK');
  };

  const renderCheckoutForm = () => (
    <div className="checkout-form">
      <h3>借書</h3>
      <div className="form-group">
        <label>選擇讀者:</label>
        <select 
          value={selectedReader} 
          onChange={(e) => setSelectedReader(e.target.value)}
          className="form-select"
        >
          <option value="">請選擇讀者</option>
          {readers.map(reader => (
            <option key={reader.id} value={reader.id}>
              {reader.display_name || reader.user?.email || '未知讀者'} ({reader.student_number || reader.reader_number || 'N/A'}) - {reader.class_info || 'N/A'}
            </option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label>選擇圖書:</label>
        <select 
          value={selectedBook} 
          onChange={(e) => setSelectedBook(e.target.value)}
          className="form-select"
        >
          <option value="">請選擇圖書</option>
          {availableBooks.map(book => (
            <option key={book.id} value={book.id}>
              {book.title} - {getBookCallNumber(book.id)}
            </option>
          ))}
        </select>
      </div>

      <button 
        className="action-button checkout-button"
        onClick={handleCheckout}
        disabled={!selectedReader || !selectedBook || isProcessing}
      >
        {isProcessing ? '處理中...' : '確認借書'}
      </button>
    </div>
  );

  const renderReturnForm = () => (
    <div className="return-form">
      <h3>還書</h3>
      <div className="borrowed-books">
        {borrowedBooks.map(book => {
          const borrowing = borrowings.find(b => (b.book_id || b.bookId) === book.id && b.status === 'borrowed');
          if (!borrowing) return null;

          return (
            <div key={book.id} className="borrowed-book-item">
              <div className="book-info">
                <h4>{book.title}</h4>
                <p>借閱者: {getReaderName(borrowing.reader_id || borrowing.readerId)}</p>
                <p>借閱日期: {formatDate(borrowing.checkout_date || borrowing.borrowDate)}</p>
                <p>到期日期: {formatDate(borrowing.due_date || borrowing.dueDate)}</p>
                {borrowing.renewed && <span className="renewed-badge">已續借</span>}
              </div>
              <button 
                className="action-button return-button"
                onClick={() => handleReturn(borrowing.id)}
                disabled={isProcessing}
              >
                還書
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderRenewForm = () => (
    <div className="renew-form">
      <h3>續借</h3>
      <div className="borrowed-books">
        {borrowedBooks.map(book => {
                  const borrowing = borrowings.find(b => b.book_id === book.id && b.status === 'borrowed');
        if (!borrowing) return null;

        const canRenew = !borrowing.renewed_count;
        const dueDate = new Date(borrowing.due_date || borrowing.dueDate);
        const today = new Date();
        const isOverdue = dueDate < today;

          return (
            <div key={book.id} className="borrowed-book-item">
              <div className="book-info">
                <h4>{book.title}</h4>
                <p>借閱者: {getReaderName(borrowing.reader_id || borrowing.readerId)}</p>
                <p>借閱日期: {formatDate(borrowing.checkout_date || borrowing.borrowDate)}</p>
                <p>到期日期: {formatDate(borrowing.due_date || borrowing.dueDate)}</p>
                {borrowing.renewed_count > 0 && <span className="renewed-badge">已續借</span>}
                {isOverdue && <span className="overdue-badge">已逾期</span>}
              </div>
              <button 
                className="action-button renew-button"
                onClick={() => handleRenew(borrowing.id)}
                disabled={!canRenew || isProcessing || isOverdue}
              >
                {canRenew ? '續借' : '已續借'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderOverdueList = () => (
    <div className="overdue-list">
      <h3>逾期圖書</h3>
      {overdueBooks.length === 0 ? (
        <p className="no-overdue">目前沒有逾期圖書</p>
      ) : (
        <div className="overdue-books">
          {overdueBooks.map(borrowing => {
            const book = books.find(b => b.id === (borrowing.book_id || borrowing.bookId));
            const reader = readers.find(r => r.id === (borrowing.reader_id || borrowing.readerId));
            const fine = calculateFine(borrowing.due_date || borrowing.dueDate);

            return (
              <div key={borrowing.id} className="overdue-book-item">
                <div className="book-info">
                  <h4>{book?.title}</h4>
                  <p>借閱者: {reader?.user?.name || reader?.name || '未知讀者'} ({reader?.reader_number || reader?.studentId || 'N/A'})</p>
                  <p>借閱日期: {formatDate(borrowing.checkout_date || borrowing.borrowDate)}</p>
                  <p>到期日期: {formatDate(borrowing.due_date || borrowing.dueDate)}</p>
                  <p className="fine-amount">罰款: ${fine}</p>
                </div>
                <div className="overdue-actions">
                  <button 
                    className="action-button return-button"
                    onClick={() => handleReturn(borrowing.id)}
                    disabled={isProcessing}
                  >
                    還書
                  </button>
                  <button 
                    className="action-button fine-button"
                    disabled={isProcessing}
                  >
                    繳納罰款
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div className="borrowing-system">
      <div className="borrowing-header">
        <button className="back-button" onClick={onBack}>
          ← 返回目錄
        </button>
        <h2>借還書系統</h2>
      </div>

      <div className="borrowing-navigation">
        <button
          className={`nav-button ${currentMode === 'checkout' ? 'active' : ''}`}
          onClick={() => setCurrentMode('checkout')}
        >
          📖 借書
        </button>
        <button
          className={`nav-button ${currentMode === 'return' ? 'active' : ''}`}
          onClick={() => setCurrentMode('return')}
        >
          📚 還書
        </button>
        <button
          className={`nav-button ${currentMode === 'renew' ? 'active' : ''}`}
          onClick={() => setCurrentMode('renew')}
        >
          🔄 續借
        </button>
        <button
          className={`nav-button ${currentMode === 'overdue' ? 'active' : ''}`}
          onClick={() => setCurrentMode('overdue')}
        >
          ⚠️ 逾期管理
        </button>
      </div>

      <div className="borrowing-content">
        {currentMode === 'checkout' && renderCheckoutForm()}
        {currentMode === 'return' && renderReturnForm()}
        {currentMode === 'renew' && renderRenewForm()}
        {currentMode === 'overdue' && renderOverdueList()}
      </div>

      <div className="borrowing-summary">
        <div className="summary-item">
          <span className="summary-label">總藏書:</span>
          <span className="summary-value">{books.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">可借:</span>
          <span className="summary-value">{availableBooks.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">已借出:</span>
          <span className="summary-value">{borrowedBooks.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">逾期:</span>
          <span className="summary-value">{overdueBooks.length}</span>
        </div>
      </div>
    </div>
  );
};

export default BorrowingSystem;
